<script lang="ts">
  import { derived } from 'svelte/store';
  import { historyStore, historySessionsStore } from '$lib/stores/history';
  import { tasksStore } from '$lib/stores/tasks';
  import { plansStore, nextScheduledPlan } from '$lib/stores/plans';
  import { aiStore } from '$lib/stores/ai';
  import { syncEngine } from '$lib/stores/sync';
  import {
    DEFAULT_REPORTING_TIME_ZONE,
    getReportingDay,
    summarizeDailyFocus,
  } from '$lib/domain/insights';

  export let now = new Date();

  let syncLabel = 'Local first';
  let focusDisplay = '0m';
  let dismissedSuggestionIds: string[] = [];
  let selectedDay = '';

  const completedTasks = derived(
    tasksStore,
    ($tasks) => $tasks.filter((task) => task.status === 'done').length,
  );
  $: today = getReportingDay(now) ?? '';
  $: reportingDay = selectedDay || today;
  $: dailyFocus = summarizeDailyFocus($historyStore, $historySessionsStore, reportingDay);
  $: scheduledPlans = $plansStore.plans.filter(
    (plan) => plan.status === 'scheduled' && getReportingDay(plan.startAt) === reportingDay,
  ).length;

  const suggestionId = (body: string, index: number) => {
    let hash = 0;
    for (const char of body) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
    return `suggestion-${index}-${hash.toString(36)}`;
  };

  const dismissSuggestion = (id: string) => {
    dismissedSuggestionIds = [...dismissedSuggestionIds, id];
  };

  const restoreSuggestions = () => {
    dismissedSuggestionIds = [];
  };

  const aiProviderLabel = (provider?: string) => {
    if (provider === 'workers-ai') return 'Workers AI';
    if (provider === 'fallback') return 'Server guidance';
    if (provider === 'local') return 'Local guidance';
    return provider ?? 'Ready';
  };

  $: syncLabel =
    $syncEngine.state === 'idle'
      ? $syncEngine.pending > 0
        ? `${$syncEngine.pending} pending`
        : 'Synced'
      : $syncEngine.state === 'syncing'
        ? 'Syncing'
        : $syncEngine.state === 'offline'
          ? 'Offline'
          : $syncEngine.state === 'disabled'
            ? 'Local first'
            : 'Sync issue';

  $: focusDisplay =
    dailyFocus.focusMinutes >= 60
      ? `${Math.floor(dailyFocus.focusMinutes / 60)}h ${String(dailyFocus.focusMinutes % 60).padStart(2, '0')}m`
      : `${dailyFocus.focusMinutes}m`;
  $: openTaskCount = $tasksStore.filter((task) => task.status === 'open').length;
  $: coachReason = `Based on up to 8 open tasks and 50 recent recorded sessions (${openTaskCount} open tasks available).`;
  $: suggestionRecords = $aiStore.cards.length
    ? $aiStore.cards
    : $aiStore.suggestions.map((body, index) => ({
        id: suggestionId(body, index),
        title: index === 0 ? 'Next move' : index === 1 ? 'Plan signal' : 'Friction check',
        body,
        status: 'ready' as const,
      }));
  $: visibleSuggestions = suggestionRecords.filter(
    (tip) => !dismissedSuggestionIds.includes(tip.id),
  );
  $: aiSourceLabel = aiProviderLabel($aiStore.provider);
  $: aiModelLabel = $aiStore.model?.replace('@cf/', '') ?? '';
  $: if (!$aiStore.suggestions.length && dismissedSuggestionIds.length) {
    dismissedSuggestionIds = [];
  }
</script>

<div
  id="insights-tabpanel"
  class="glass-panel insights-panel"
  role="tabpanel"
  aria-labelledby="insights-title"
  data-testid="insights-panel"
>
  <div class="panel-header">
    <div>
      <p class="kicker">Insights</p>
      <h2 id="insights-title">Your progress</h2>
    </div>
    <span class="select-chip">{reportingDay === today ? 'Today' : reportingDay}</span>
  </div>

  <div class="insights-period">
    <label>
      <span>Reporting day</span>
      <input
        type="date"
        aria-label="Insights reporting day"
        value={reportingDay}
        on:change={(event) => (selectedDay = event.currentTarget.value)}
      />
    </label>
    {#if selectedDay}
      <button class="button ghost small" on:click={() => (selectedDay = '')}>Today</button>
    {/if}
    <small>{DEFAULT_REPORTING_TIME_ZONE}</small>
  </div>

  <div class="stats-grid">
    <div class="stat">
      <span class="stat-icon blue">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="8" />
          <path d="M12 7v5l3 2" />
        </svg>
      </span>
      <div>
        <p>{focusDisplay}</p>
        <h3>Completed focus</h3>
        <small>{dailyFocus.focusSessions} focus sessions · {dailyFocus.breakMinutes}m breaks</small>
      </div>
    </div>
    <div class="stat">
      <span class="stat-icon green">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="8" />
          <path d="m8.5 12 2.2 2.2 4.8-5" />
        </svg>
      </span>
      <div>
        <p>{$completedTasks}</p>
        <h3>Done tasks · all time</h3>
      </div>
    </div>
    <div class="stat">
      <span class="stat-icon amber">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M5 16 10 11l3 3 6-7" />
          <path d="M15 7h4v4" />
        </svg>
      </span>
      <div>
        <p>{scheduledPlans}</p>
        <h3>Scheduled plans</h3>
      </div>
    </div>
    <div class="stat">
      <span class="stat-icon coral">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M20 12a8 8 0 1 1-2.34-5.66" />
          <path d="M20 4v6h-6" />
        </svg>
      </span>
      <div>
        <p>{syncLabel}</p>
        <h3>Sync status</h3>
      </div>
    </div>
  </div>

  {#if dailyFocus.unclassifiedSessions}
    <p class="notice">
      {dailyFocus.unclassifiedMinutes} recorded minutes have no session type and are excluded from focus
      time.
    </p>
  {/if}

  <div class="rail-section ai-section">
    <div class="rail-section__header">
      <div>
        <div class="kicker">AI Coach</div>
        <strong>Focus guidance</strong>
      </div>
      <div class="ai-header-actions">
        {#if $aiStore.provider}
          <span
            class="ai-source-chip"
            class:fallback={$aiStore.provider === 'fallback' || $aiStore.provider === 'local'}
            title={aiModelLabel ? `Model: ${$aiStore.model}` : undefined}
          >
            {aiSourceLabel}
            {#if aiModelLabel}
              <small>{aiModelLabel}</small>
            {/if}
          </span>
        {/if}
        <button
          class="button ghost icon-button"
          aria-label="Refresh AI suggestions"
          disabled={$aiStore.loading}
          on:click={() => aiStore.fetchSuggestions()}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20 12a8 8 0 1 1-2.34-5.66" />
            <path d="M20 4v6h-6" />
          </svg>
        </button>
      </div>
    </div>

    {#if $aiStore.notice}
      <p class="notice" role="status">{$aiStore.notice}</p>
    {/if}

    {#if visibleSuggestions.length}
      <div class="list">
        {#each visibleSuggestions as tip}
          <div
            class="coach-card ai-card"
            data-status={tip.status}
            aria-busy={tip.status === 'loading'}
          >
            <div class="coach-card__main">
              <span class="stat-icon blue mini">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="m12 3 1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5L12 3Z" />
                </svg>
              </span>
              <div>
                <span class="confidence-chip">
                  {tip.status === 'loading'
                    ? 'Updating'
                    : tip.status === 'failed'
                      ? 'Needs retry'
                      : 'Likely useful'}
                </span>
                <span class="ai-card__title">{tip.title}</span>
                <strong><bdi>{tip.body}</bdi></strong>
              </div>
            </div>
            <p class="coach-card__reason">
              {#if tip.status === 'loading'}
                Card-level update in progress.
              {:else if tip.status === 'failed'}
                {$aiStore.error ?? 'The coach could not update this card.'}
              {:else}
                Why this appeared: {coachReason} Draft only; no automatic changes are made.
              {/if}
            </p>
            <div class="coach-card__actions">
              <button
                class="button ghost small"
                aria-label="Dismiss suggestion"
                disabled={tip.status === 'loading'}
                on:click={() => dismissSuggestion(tip.id)}
              >
                Dismiss
              </button>
              <button
                class="button secondary small"
                disabled={$aiStore.loading}
                on:click={() => aiStore.fetchSuggestions()}>Refresh</button
              >
            </div>
          </div>
        {/each}
      </div>
    {:else if $aiStore.suggestions.length}
      <div class="notice">
        Suggestions dismissed.
        <button class="text-button" on:click={restoreSuggestions}>Undo</button>
      </div>
    {:else}
      <div class="coach-row">
        <span class="stat-icon green mini">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M12 3v4M12 17v4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M3 12h4M17 12h4M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8"
            />
          </svg>
        </span>
        <span
          >Get three suggestions from your task and session patterns. Local guidance is available
          offline.</span
        >
      </div>
    {/if}

    <button
      class="button secondary generate-button"
      disabled={$aiStore.loading}
      on:click={() => aiStore.fetchSuggestions()}
    >
      {$aiStore.suggestions.length ? 'Regenerate' : 'Generate'}
    </button>
  </div>

  <div class="rail-section next-plan">
    <div class="rail-section__header">
      <div>
        <div class="kicker">Next plan</div>
        <strong><bdi>{$nextScheduledPlan?.title ?? 'No scheduled plans'}</bdi></strong>
      </div>
      {#if $nextScheduledPlan}
        <span class="tag">{$nextScheduledPlan.autoStart ? 'Auto-start' : 'Manual'}</span>
      {/if}
    </div>

    {#if $nextScheduledPlan}
      <div class="timeline-row">
        <span
          >{new Date($nextScheduledPlan.startAt).toLocaleTimeString(undefined, {
            hour: 'numeric',
            minute: '2-digit',
          })}</span
        >
        <strong><bdi>{$nextScheduledPlan.title}</bdi></strong>
        <small>{$nextScheduledPlan.configSnapshot.workDuration}m</small>
      </div>
    {:else}
      <div class="empty-state slim">
        <span>Use the schedule action to create the next focus block.</span>
      </div>
    {/if}
  </div>
</div>

<style>
  .insights-period {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 8px;
    margin: 14px 0;
  }

  .insights-period label {
    display: grid;
    min-width: 0;
    gap: 4px;
    font-size: 12px;
    color: var(--color-text-muted);
  }

  .insights-period input {
    width: 9.5rem;
    max-width: 100%;
    font: inherit;
  }

  .insights-period small,
  .stat small {
    color: var(--color-text-muted);
    font-size: 11px;
    line-height: 1.5;
  }
</style>
